import {
	IRegEdSubmissionView,
	isRegEdDecisionCode,
	REGED_REVISABLE_DECISION_CODE,
	RegEdSubmissionStatus,
	useAccountUsesRegEd,
	useRegEdSubmissionsQuery,
} from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { getDefaultDateStringValue } from '../../../../models/UiUtils';
import { Button } from '../../../components-v2/Button';
import { styleSheet } from './styles';

interface IProps {
	parentId?: string;
	impersonationContext?: Api.IImpersonationContext;
	/** Renders "Revise & Resubmit" on a revisable rejection; the caller resubmits with `revisesApprovalRequestId`. */
	onRevise?: (submission: IRegEdSubmissionView) => void;
	/** Card chrome belongs to the host — this panel sits in three surfaces with different idioms. */
	styles?: StyleDeclarationValue[];
}

const getStatusPresentation = (status?: RegEdSubmissionStatus): { label: string; tone: StyleDeclarationValue } => {
	switch (status) {
		case RegEdSubmissionStatus.Approved:
			return { label: 'Approved', tone: styleSheet.toneSuccess };
		case RegEdSubmissionStatus.Rejected:
			return { label: 'Rejected', tone: styleSheet.toneDanger };
		case RegEdSubmissionStatus.Pending:
		case RegEdSubmissionStatus.Submitting:
			return { label: 'In review', tone: styleSheet.toneInfo };
		case RegEdSubmissionStatus.Withdrawn:
			return { label: 'Withdrawn', tone: styleSheet.toneNeutral };
		case RegEdSubmissionStatus.Failed:
			return { label: 'Submission failed', tone: styleSheet.toneDanger };
		case RegEdSubmissionStatus.Expired:
			return { label: 'Review expired', tone: styleSheet.toneWarning };
		default:
			return { label: 'Status unknown', tone: styleSheet.toneWarning };
	}
};

const getAttentionText = (status?: RegEdSubmissionStatus): string | null => {
	switch (status) {
		case RegEdSubmissionStatus.Failed:
			return 'The submission to your compliance system failed. Please contact support.';
		case RegEdSubmissionStatus.Unknown:
			return "We couldn't confirm this submission reached your compliance system. Contact support if the status doesn't update.";
		case RegEdSubmissionStatus.Expired:
			return 'The compliance review aged out without a decision. Follow up with your reviewer.';
		default:
			return null;
	}
};

/**
 * Replaces the internal approve/reject actions on RegEd accounts — AdTrax owns the decision, so this
 * only surfaces state. Renders nothing unless the account uses RegEd and the parent has a submission.
 */
export const RegEdComplianceStatus: React.FC<IProps> = ({ parentId, impersonationContext, onRevise, styles = [] }) => {
	const usesRegEd = useAccountUsesRegEd(impersonationContext);
	const { data: submissions, isLoading } = useRegEdSubmissionsQuery({
		parentId,
		impersonationContext,
		enabled: usesRegEd,
	});

	if (!usesRegEd) {
		return null;
	}

	if (isLoading) {
		return (
			<div className={css(styleSheet.card, ...styles)}>
				<div className={css(styleSheet.title)}>Compliance Review</div>
				<div className={css(styleSheet.detail)}>Checking compliance status…</div>
			</div>
		);
	}

	// Newest-first — the first row is the latest submission chain.
	const latest = submissions?.[0];
	if (!latest) {
		return null;
	}

	const presentation = getStatusPresentation(latest.status);
	const attentionText = getAttentionText(latest.status);
	const revisionRequested =
		latest.status === RegEdSubmissionStatus.Rejected &&
		isRegEdDecisionCode(latest.decisionCode, REGED_REVISABLE_DECISION_CODE);
	const reviewNote = latest.reviewNotes?.find(note => !!note.noteText?.trim())?.noteText?.trim();

	return (
		<div className={css(styleSheet.card, ...styles)}>
			<div className={css(styleSheet.title)}>Compliance Review</div>
			<div className={css(styleSheet.status, presentation.tone)}>{presentation.label}</div>
			{latest.creationDate ? (
				<div className={css(styleSheet.detail)}>Submitted {getDefaultDateStringValue(latest.creationDate)}</div>
			) : null}
			{latest.trackingNumber ? <div className={css(styleSheet.detail)}>Tracking #: {latest.trackingNumber}</div> : null}
			{revisionRequested ? (
				<div className={css(styleSheet.detail)}>
					{reviewNote
						? `Your reviewer requested revisions: ${reviewNote}`
						: 'Your reviewer requested revisions before this can be approved.'}
				</div>
			) : latest.status === RegEdSubmissionStatus.Rejected ? (
				<>
					{/* The reason matters most on a rejection that can't be revised — it's the only thing the
					    advisor gets to learn from, since the piece is terminal. */}
					{reviewNote ? <div className={css(styleSheet.detail)}>Your reviewer's reason: {reviewNote}</div> : null}
					{latest.decisionCode ? <div className={css(styleSheet.detail)}>Decision: {latest.decisionCode}</div> : null}
				</>
			) : null}
			{attentionText ? <div className={css(styleSheet.detail)}>{attentionText}</div> : null}
			{revisionRequested && onRevise ? (
				<Button variant='secondary' size='sm' styles={[styleSheet.reviseButton]} onClick={() => onRevise(latest)}>
					Revise & Resubmit
				</Button>
			) : null}
		</div>
	);
};
